import { DateTime } from 'luxon';
import { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';

import { useUser } from '@work4all/data';
import { settings, useSetting } from '@work4all/data/lib/settings';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { ProjectTimeMaskInit } from '../containers/time-tracker/ProjectTimeMask';
import { useTimeTracker } from '../containers/time-tracker/use-time-tracker';
import { useSelectedDay } from '../containers/time-tracker/useTimeTrackingOverview';

export const useTicketToTimeTracking = (
  ticket: Ticket
): ProjectTimeMaskInit => {
  const user = useUser();
  const selectedDayOptions = useMemo(() => {
    const today = DateTime.now().startOf('day');

    return {
      from: today.startOf('day').toJSDate(),
      to: today.endOf('day').toJSDate(),
      userId: user.benutzerCode,
    };
  }, [user.benutzerCode]);

  const { selectedDay } = useSelectedDay(selectedDayOptions);
  const {
    value: shouldPrefillTimeTrackingCommentFromTicketProblemDescription,
  } = useSetting(
    settings.shouldPrefillTimeTrackingCommentFromTicketProblemDescription()
  );

  const { result } = useTimeTracker({ amplitudeEntryPoint: 'TicketMask' });
  const { state, presentSince } = result || {
    state: 'initial',
    presentSince: undefined,
  };

  const { startDateTime, endDateTime } = useMemo(() => {
    let start: DateTime | undefined;
    let end: DateTime | undefined;
    if (selectedDay) {
      const previousEntry = selectedDay.timeTrackingItems
        .map((x) => x.endDateTime)
        .sort((a, b) => {
          return new Date(a) > new Date(b) ? -1 : 1;
        })?.[0];
      end = DateTime.now();

      if (previousEntry) {
        // case 3. next entry
        start = DateTime.fromISO(previousEntry);
      } else {
        const isDayStarted = state !== 'initial';
        if (isDayStarted) {
          // case 2. first entry - day started
          start = presentSince;
        } else {
          // case 1. first entry - no day started
          start = roundBottom(DateTime.now().minus({ hours: 1 }), 15);
        }
      }
    }

    if (start > end) {
      console.warn(
        'There were inconsitent data. Check time tracker and time tracking overview'
      );
      start = roundBottom(DateTime.now().minus({ hours: 1 }), 15);
    }

    return {
      startDateTime: start?.startOf('minute')?.toJSDate(),
      endDateTime: end?.startOf('minute')?.toJSDate(),
    };
    // we want to rerender if value change not refenrece to presentSince
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, state, presentSince?.toISO()]);

  return useMemo(() => {
    let comment: string | undefined = undefined;
    let customer: Customer | undefined = undefined;

    if (
      ticket?.problemDescription &&
      shouldPrefillTimeTrackingCommentFromTicketProblemDescription
    ) {
      comment = sanitizeHtml(ticket.problemDescription, { allowedTags: [] });
    }

    if (ticket?.businessPartner?.businessPartnerType === SdObjType.KUNDE) {
      customer = ticket?.businessPartner.data as Customer;
    }

    return {
      project: ticket?.project,
      projectProcess: ticket?.projectProcess,
      customer,
      ticket,
      comment,
      startDateTime,
      endDateTime,
      contract: ticket?.contract,
      position: ticket?.position,
    };
  }, [
    ticket,
    shouldPrefillTimeTrackingCommentFromTicketProblemDescription,
    startDateTime,
    endDateTime,
  ]);
};

const roundBottom = (date: DateTime, step: number) => {
  const minute = Math.floor(date.get('minute') / step) * step;
  return date.set({ minute });
};
